import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BadgeCheck, Clock, XCircle, IdCard, Download } from "lucide-react";

const CARD_TEXT = { generated: "Card being prepared", printed: "Card printed, ready for issue", issued: "Card issued to you" };

export function VerificationPanel() {
  const [v, setV] = useState(null);
  const [pdf, setPdf] = useState(null);
  useEffect(() => { api.get("/worker/verification").then((r) => setV(r.data)).catch(() => {}); }, []);
  useEffect(() => () => pdf && URL.revokeObjectURL(pdf), [pdf]);
  if (!v) return null;
  const approved = ["APPROVED", "CARD_PRINTED", "ISSUED"].includes(v.status);
  const openCard = async () => {
    try {
      const r = await api.get("/worker/id-card.pdf", { responseType: "blob" });
      setPdf(URL.createObjectURL(r.data));
    } catch { toast.error("Your ID card is not available right now. Please try again later."); }
  };

  if (v.status === "PENDING") return (
    <Card className="p-4 bg-slate-800 border-slate-700" data-testid="wv-pending">
      <div className="flex gap-3">
        <Clock className="text-amber-400 shrink-0" size={20}/>
        <div>
          <div className="font-semibold text-slate-100">Application submitted · Verification pending</div>
          <p className="text-sm text-slate-400 mt-1">A Karmivo Admin is reviewing your details. Your Worker ID will be issued after verification.</p>
        </div>
      </div>
    </Card>
  );
  if (v.status === "REJECTED") return (
    <Card className="p-4 bg-red-500/10 border-red-500/30" data-testid="wv-rejected">
      <div className="flex gap-3"><XCircle className="text-red-400 shrink-0" size={20}/>
        <div><div className="font-semibold text-red-200">Application not approved</div>
          <p className="text-sm text-red-200/80 mt-1" data-testid="wv-reject-reason">Reason: {v.rejected_reason}</p>
          <p className="text-xs text-slate-400 mt-2">Contact Karmivo support on 9636873585 for help.</p></div></div>
    </Card>
  );
  if (!approved) return (
    <Card className="p-4 bg-red-500/10 border-red-500/30 text-sm text-red-200" data-testid="wv-blocked">Your Worker ID is {v.status.toLowerCase()}. Contact Karmivo support.</Card>
  );
  return (
    <Card className="p-4 bg-slate-800 border-slate-700" data-testid="wv-approved">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider"><BadgeCheck size={16}/>Verified worker</div>
          <div className="font-display text-2xl font-bold text-orange-400 mt-1" data-testid="wv-worker-code">{v.worker_code || "ID being issued"}</div>
        </div>
        {v.worker_code && <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openCard} data-testid="wv-view-card"><IdCard size={15} className="mr-1"/>View ID card</Button>}
      </div>
      {v.card && (
        <div className="mt-3 text-sm text-slate-300 grid grid-cols-2 gap-2">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Physical card</div><div data-testid="wv-card-status">{CARD_TEXT[v.card.status] || v.card.status}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Card issue date</div><div data-testid="wv-card-date">{v.card.issued_at ? new Date(v.card.issued_at).toLocaleDateString("en-IN") : "Not issued yet"}</div></div>
        </div>
      )}
      {v.card?.status === "printed" && (
        <div className="mt-3 rounded-lg border border-orange-500/40 bg-orange-500/10 p-3 text-sm text-orange-100" data-testid="wv-pickup">
          <div className="font-semibold">Your card is ready to collect</div>
          <div className="text-orange-100/80 mt-0.5">{v.pickup?.pickup_location
            ? `${v.pickup.pickup_location}${v.pickup.pickup_hours ? ` · ${v.pickup.pickup_hours}` : ""}. Bring a photo ID.`
            : "Call Karmivo Support on 9636873585 to arrange pickup."}</div>
        </div>
      )}
      {pdf && (
        <div className="mt-4" data-testid="wv-card-preview">
          <iframe title="ID card" src={pdf} className="w-full h-72 rounded-lg bg-white"/>
          <a href={pdf} download="karmivo-id-card.pdf" className="inline-flex items-center gap-1 text-sm text-orange-400 mt-2" data-testid="wv-card-download"><Download size={14}/>Download</a>
        </div>
      )}
    </Card>
  );
}
